import Link from 'next/link'

export default function PrivacyPolicy() {
  return (
    <main className="bg-white text-slate-900">
      <div className="mx-auto max-w-3xl px-6 py-20">
        <Link href="/" className="text-sm font-semibold text-cyan-600 transition hover:text-cyan-700">
          ← Back to home
        </Link>
        <h1 className="mt-6 text-4xl font-semibold leading-tight">Privacy Policy</h1>
        <p className="mt-4 text-sm uppercase tracking-wide text-slate-500">Effective as of {new Date().toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <section className="mt-10 space-y-6 text-base leading-relaxed text-slate-600">
          <p>
            Lernio Labs ("Lernio", "we", "our") provides an AI-enabled learning platform that helps individuals and
            teams transform study materials into actionable insights. This Privacy Policy explains how we collect, use,
            and protect personal information when you access our websites, apps, and related services (collectively, the
            "Services").
          </p>

          <div>
            <h2 className="text-xl font-semibold text-slate-900">1. Information We Collect</h2>
            <p className="mt-3">
              We collect information you provide directly, such as account details (name, email address, password),
              uploaded documents, and communication preferences. When you use the Services we also collect usage data
              (e.g., features accessed, device and browser information, IP address) to improve performance and security.
              For paid plans we rely on Stripe to process billing information; payment details never touch Lernio servers.
            </p>
            <p className="mt-3">
              If you connect third-party integrations (e.g., Google Drive, Notion) we access data only as necessary to
              deliver requested functionality and in accordance with the permissions you grant.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-slate-900">2. How We Use Information</h2>
            <p className="mt-3">
              We use personal information to provide, maintain, and improve the Services, including creating study
              outputs, managing accounts, delivering customer support, and communicating about new features or Policy
              updates. We may anonymize and aggregate usage insights to enhance product capabilities and develop new
              offerings. We never sell personal information.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-slate-900">3. Sharing and Disclosure</h2>
            <p className="mt-3">
              We share data with trusted service providers (e.g., hosting, analytics, email delivery, payment processing)
              who process information on our behalf in compliance with contractual and legal obligations. We may disclose
              information if required by law, to protect user safety, or in connection with a corporate transaction. If we
              integrate with a third-party platform at your request, data shared is governed by that provider&apos;s terms.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-slate-900">4. Data Retention and Security</h2>
            <p className="mt-3">
              We retain personal information for as long as necessary to fulfil the purposes described in this Policy or
              as required by law. We implement technical and organizational safeguards—including encryption, access
              controls, and monitoring—to protect against unauthorized access, loss, or misuse. Despite safeguards, no
              system is 100% secure; please notify us immediately if you suspect an issue.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-slate-900">5. Your Choices</h2>
            <p className="mt-3">
              You can update profile information from account settings, manage marketing emails through unsubscribe links,
              or request deletion of your account by contacting us at <a href="mailto:privacy@lernio.io" className="text-cyan-600">privacy@lernio.io</a>.
              Certain requests may affect your ability to use the Services. For legal reasons, we may retain limited
              information even after deletion.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-slate-900">6. International Transfers</h2>
            <p className="mt-3">
              We operate globally and may transfer personal information to countries different from your own. When we do
              so, we rely on lawful transfer mechanisms and ensure that recipients offer adequate protections consistent
              with applicable privacy laws.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-slate-900">7. Children&apos;s Privacy</h2>
            <p className="mt-3">
              Lernio is not directed to children under 13, and we do not knowingly collect personal information from them.
              If you believe a child has provided us personal data, contact us and we will take appropriate steps to
              remove the information.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-slate-900">8. Changes to this Policy</h2>
            <p className="mt-3">
              We may update this Policy to reflect changes in our practices or for operational, legal, or regulatory
              reasons. We will post an updated version and indicate the effective date. Material changes may be
              communicated via email or in-app notifications.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-slate-900">9. Contact Us</h2>
            <p className="mt-3">
              If you have questions or requests regarding this Policy, contact us at
              <a href="mailto:privacy@lernio.io" className="text-cyan-600"> privacy@lernio.io</a> or by mail at Lernio Labs, [Your Address].
            </p>
          </div>
        </section>
      </div>
    </main>
  )
}
